import {
  Text,
  Box,
  Link,
  IconFa,
  Badge,
  Radio,
} from "@cruk/cruk-react-components";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { type UseFormReturn } from "react-hook-form";
import { numberWithCommas } from "@fwa/src/utils/formatUtils";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/DonatePageGiftAid/styles.module.css";

const giftaidImg = "/assets/images/img/giftaid.png";

type GiftAidFieldValues = {
  personalGiftAid: string | null;
  noBenefitReceived: string | null;
};

const addTwentyFivePercent = (number: number | string) => {
  const sum =
    parseFloat(number as string) * 0.25 + parseFloat(number as string);
  if ((number as number) > 1) {
    return parseFloat(sum.toString()).toFixed(2);
  }
  return "0";
};

export const DonatePageGiftAid = <T extends GiftAidFieldValues>(
  props: UseFormReturn<T> & {
    personalGiftAid: string | null;
    noBenefitReceived: string | null;
    amount: string;
  },
) => {
  const { amount, noBenefitReceived, personalGiftAid } = props;
  const assertProps = props as unknown as UseFormReturn<GiftAidFieldValues>;
  const { register } = assertProps;

  return (
    <fieldset className={sharedStyles["fieldset"]}>
      <Text>
        With Gift Aid, your donation of
        <span
          className="text-props"
          data-text-weight="heavy"
          data-text-size="xl"
        >
          &nbsp;£{parseFloat(amount) > 1 ? numberWithCommas(amount) : "0"}
          &nbsp;
        </span>
        would be worth{" "}
        <span
          className="text-props"
          data-text-weight="heavy"
          data-text-size="xl"
        >
          £{numberWithCommas(addTwentyFivePercent(amount))}{" "}
        </span>{" "}
        at no extra cost to you
      </Text>

      <img className={styles["image"]} src={giftaidImg} alt="Giftaid it" />
      <Text>
        If you pay UK tax, Gift Aid your donation and the Government will give
        us 25% extra, recovered from the tax that you pay.
      </Text>
      <Text textWeight="labels">Check if you can Gift Aid your donation:</Text>
      <div className={styles["radio-wrapper"]}>
        <Radio
          {...register("personalGiftAid")}
          value="false"
          checked={personalGiftAid === "false"}
        >
          Some or all of this money has come from someone else (e.g. a
          collection, friends or a business)
        </Radio>
      </div>
      <div className={styles["radio-wrapper"]}>
        <Radio
          {...register("personalGiftAid")}
          value="true"
          checked={personalGiftAid === "true"}
        >
          This is all my own money
        </Radio>
      </div>

      {personalGiftAid === "true" ? (
        <>
          <Text textWeight="labels">And...</Text>
          <div className={styles["radio-wrapper"]}>
            <Radio
              {...register("noBenefitReceived")}
              value="false"
              checked={noBenefitReceived === "false"}
            >
              I am receiving something in exchange for this money (e.g. from a
              sale, entry into an event or the chance to win something)
            </Radio>
          </div>
          <div className={styles["radio-wrapper"]}>
            <Radio
              {...register("noBenefitReceived")}
              value="true"
              checked={noBenefitReceived === "true"}
            >
              I am not receiving anything in exchange for this money
            </Radio>
          </div>
        </>
      ) : null}
      <Box aria-live="polite">
        {personalGiftAid === "false" &&
        (noBenefitReceived === null || noBenefitReceived === "true") ? (
          <span className={styles["gift-message-span"]}>
            <Badge backgroundColor="warning" textColor="text-dark">
              !
            </Badge>
            Sorry, you can only Gift Aid money you have paid tax on. Continue
            without Gift Aid - if you have a sponsorship form we can claim Gift
            Aid when we receive it.
          </span>
        ) : personalGiftAid === "true" && noBenefitReceived === "false" ? (
          <span className={styles["gift-message-span"]}>
            <Badge backgroundColor="warning" textColor="text-dark">
              !
            </Badge>
            Sorry, you can only Gift Aid money that the Government categorises
            as a gift.
          </span>
        ) : personalGiftAid === "false" && noBenefitReceived === "false" ? (
          <span className={styles["gift-message-span"]}>
            <Badge backgroundColor="warning" textColor="text-dark">
              !
            </Badge>{" "}
            Sorry, you can only Gift Aid money you have paid tax on, and that
            the Government categorises as a gift.
          </span>
        ) : personalGiftAid === "true" && noBenefitReceived === "true" ? (
          <span className={styles["gift-message-span"]}>
            <Text as="span" textColor="success" marginTop="xxs">
              <IconFa faIcon={faCheckCircle} size="2em" />
            </Text>
            <Box>
              <span>Great news - your donation is eligible for Gift Aid!</span>
              <Text>
                {`By selecting 'Continue with Gift Aid' I declare that I want to
                Gift Aid this donation and any donations I make in the future or
                have made in the past 4 years to Cancer Research UK. I also
                declare that I am a UK taxpayer and understand that if I pay
                less Income Tax and/or Capital Gains Tax in the current tax year
                than the amount of Gift Aid claimed on all my donations, it is
                my responsibility to pay any difference.`}
              </Text>
            </Box>
          </span>
        ) : null}
        <Text marginTop="xxl">
          By continuing I confirm the details I have entered are correct and
          that I have read and agreed to the{" "}
          <Link
            href="https://www.cancerresearchuk.org/terms-and-conditions/online-fundraising-terms-and-conditions"
            data-cta-type="link-terms-and-conditions"
          >
            terms and conditions
          </Link>{" "}
          and the{" "}
          <Link
            href="https://www.cancerresearchuk.org/privacy-statement?_ga=2.47020903.1570715431.1612197448-309244759.1606746245"
            data-cta-type="link-privacy-statement"
          >
            privacy statement
          </Link>
          .
        </Text>
      </Box>
    </fieldset>
  );
};

export default DonatePageGiftAid;
